import type { InfiniteData, QueryKey } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiPost } from "../client";
import type { Page, Post } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";
import type { FeedTab } from "./qk";

const FEED_TABS: FeedTab[] = ["for-you", "following", "trending"];

/** Every cached infinite-list key that can contain a given post's card. */
function postListKeys(context: { authorUsername: string; communitySlug?: string }): QueryKey[] {
  const keys: QueryKey[] = [...FEED_TABS.map((tab) => qk.feed(tab)), qk.creators.posts(context.authorUsername)];
  if (context.communitySlug) keys.push(qk.communities.posts(context.communitySlug));
  return keys;
}

function updatePostInPages(postId: string, updater: (post: Post) => Post) {
  return (previous: InfiniteData<Page<Post>> | undefined): InfiniteData<Page<Post>> | undefined => {
    if (!previous) return previous;
    return {
      ...previous,
      pages: previous.pages.map((page) => ({
        ...page,
        items: page.items.map((post) => (post.id === postId ? updater(post) : post)),
      })),
    };
  };
}

export interface CreatePostVariables {
  body: string;
  mediaKey?: string;
  communitySlug?: string;
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: CreatePostVariables) => apiPost<Post>("/api/posts", variables),
    onSuccess: () => {
      // A brand-new post doesn't exist in any cached page yet — refetch
      // rather than optimistically splice it into an arbitrary feed tab.
      void queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}

export interface LikePostVariables {
  postId: string;
  liked: boolean;
  authorUsername: string;
  communitySlug?: string;
}

export function useLikePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, liked }: LikePostVariables) =>
      liked ? apiPost<{ liked: boolean; likes: number }>(`/api/posts/${postId}/like`) : apiDelete<{ liked: boolean; likes: number }>(`/api/posts/${postId}/like`),
    ...createOptimisticHandlers<LikePostVariables>({
      queryClient,
      action: "like-post",
      affectedKeys: (variables) => postListKeys(variables),
      apply: (variables) =>
        postListKeys(variables).map((queryKey) => ({
          queryKey,
          updater: updatePostInPages(variables.postId, (post) => ({
            ...post,
            liked: variables.liked,
            likes: variables.liked ? post.likes + 1 : Math.max(0, post.likes - 1),
          })),
        })),
    }),
  });
}

export interface BookmarkPostVariables {
  postId: string;
  bookmarked: boolean;
  authorUsername: string;
  communitySlug?: string;
}

export function useBookmarkPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, bookmarked }: BookmarkPostVariables) =>
      bookmarked
        ? apiPost<{ bookmarked: boolean }>(`/api/posts/${postId}/bookmark`)
        : apiDelete<{ bookmarked: boolean }>(`/api/posts/${postId}/bookmark`),
    ...createOptimisticHandlers<BookmarkPostVariables>({
      queryClient,
      action: "bookmark-post",
      affectedKeys: (variables) => postListKeys(variables),
      apply: (variables) =>
        postListKeys(variables).map((queryKey) => ({
          queryKey,
          updater: updatePostInPages(variables.postId, (post) => ({ ...post, bookmarked: variables.bookmarked })),
        })),
    }),
  });
}
